'use client';

import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { PinDrop, Search } from '@mui/icons-material';
import { InputAdornment, List, ListItemButton, ListItemText, TextField, Typography } from '@mui/material';
import { isFalsy } from 'utility-types';
import { findAppStores } from '@/api/appstore.api';
import type { AppStoreDto } from '@/interfaces/service/appstore/appstore.interface';
import { useAppStoreStore } from '@/stores/appstore.store';
import { REVIEW_ROUTES } from '@/constants/path.constants';
import { throttle } from '@/utils/base.util';
import { isCompleteKoreanCharacter } from '@/utils/string.korean.util';
import EmptyCompnent from '@/components/EmptyComponent';
import SearchModal from '@/components/modal/search/SearchModal';

const StoreSearchBar = () => {
  const router = useRouter();

  const searchInput = useRef<HTMLInputElement>();
  const searchContainerRef = useRef<HTMLDivElement>(null);

  const [searchWord, setSearchWord] = useState('');
  const [searchResults, setSearchResults] = useState<AppStoreDto[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);

  const [showSearchModal, setShowSearchModal] = useState(false);

  const { updateAppStores } = useAppStoreStore((state) => state);

  const getAppStoresByUserInputs = async (text: string) => {
    const res = await findAppStores(text);
    if (res) {
      setSearchResults(res.length > 5 ? res.slice(0, 5) : res);
    } else {
      setSearchResults([]);
    }
  };
  const throttleGetAppStore = throttle(getAppStoresByUserInputs);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node) && searchWord.length > 0) {
        setSearchResults([]);
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [searchWord]);

  const checkStore = async (e: any) => {
    const text = e.target.value;

    setSearchWord(text);

    if (isFalsy(text) || text?.length < 2 || text[text?.length - 1] === ' ') {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    // 2글자 이상이면 무조건 드롭다운 표시
    setShowDropdown(true);

    const isCompleteCharacter = text.split('').findIndex((letter) => !isCompleteKoreanCharacter(letter)) < 0;

    if (isCompleteCharacter) {
      // throttle 함수만 사용하여 중복 호출 방지
      throttleGetAppStore(text);
    } else {
      // 완전한 한글이 아니어도 드롭다운은 표시하고 검색 결과는 빈 배열
      setSearchResults([]);
    }
  };

  const openSearchModal = () => {
    setShowSearchModal(true);
  };

  // 검색 실행 함수 분리
  const executeSearch = () => {
    if (searchWord && searchWord.length > 0) {
      openSearchModal();
    }
  };

  const selectStore = (store: AppStoreDto) => {
    updateAppStores(store);
    // 검색 성공 후에도 검색어 유지
    if (store.status === 'STABLE') {
      router.push(REVIEW_ROUTES.getStoreDashboard(store.storeId));
    } else {
      router.push(REVIEW_ROUTES.getReviews(store.storeId));
    }
  };

  const hasGradientBorder = !showDropdown || searchWord?.length === 0;
  const gradientBorderStyle = hasGradientBorder
    ? {
        background: 'linear-gradient(47deg, #6B00D7 6.13%, #623CFF 46.83%, #3B4BFF 87.53%)',
        padding: '1px',
        borderRadius: '5px'
      }
    : {};
  // eslint-disable-next-line max-len
  const dropdownClasses = showDropdown && searchWord?.length > 0 ? 'shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] mb-[10px] overflow-hidden' : '';
  const containerClassName = `w-[327px] md:w-[560px] bg-white rounded-lg ${dropdownClasses}`;

  return (
    <>
      <div className="flex flex-col items-center">
        <div ref={searchContainerRef} className={hasGradientBorder ? '' : containerClassName} style={gradientBorderStyle}>
          {hasGradientBorder && <div className={containerClassName}> </div>}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (searchWord && searchWord.length > 0) {
                openSearchModal();
              }
            }}
          >
            <TextField
              inputRef={searchInput}
              variant="outlined"
              fullWidth={true}
              value={searchWord}
              sx={{
                backgroundColor: 'white',
                width: { xs: '327px', md: '560px' },
                height: '56px',
                ...(!showDropdown || searchWord?.length === 0
                  ? {
                      borderRadius: '4px'
                    }
                  : {
                      borderBottom: '1px solid #E5E7EB'
                    }),
                '& fieldset': { border: 'none', padding: 0 },
                padding: '16px',
                display: 'flex',
                alignItems: 'center',
                '& .MuiInputBase-root': {
                  display: 'flex',
                  alignItems: 'center',
                  height: '100%'
                },
                '& .MuiInputBase-input': {
                  fontSize: '16px',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  lineHeight: '24px',
                  padding: 0
                },
                '& .MuiInputBase-input::placeholder': {
                  color: '#9CA3AF',
                  fontSize: '16px',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  lineHeight: '24px',
                  opacity: 1
                }
              }}
              color="primary"
              placeholder="지금 바로 병원 평판을 검색해보세요."
              onFocus={() => {
                if (searchWord.length > 0) {
                  setShowDropdown(true);
                }
              }}
              slotProps={{
                input: {
                  className: 'p-0 m-0',
                  startAdornment: (
                    <InputAdornment position="start" sx={{ marginRight: '8px' }}>
                      <Search
                        onClick={() => {
                          if (searchWord && searchWord.length > 0) {
                            openSearchModal();
                          }
                        }}
                        sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#6B00D7' }}
                      />
                    </InputAdornment>
                  )
                }
              }}
              onChange={checkStore}
            />

            {showDropdown && searchWord?.length > 0 && (
              <List className="w-[327px] md:w-[560px] mt-2">
                <ListItemButton key={-1} onClick={() => openSearchModal()}>
                  <div className="flex flex-row gap-1">
                    <Search sx={{ color: '#6B00D7' }} />
                    <Typography variant="subtitle1" className="font-semibold">
                      {searchWord}
                    </Typography>
                    <Typography variant="subtitle1" color="textSecondary">
                      - 검색하기
                    </Typography>
                  </div>
                </ListItemButton>
                {searchResults.map((AppStore: AppStoreDto) => (
                  <ListItemButton
                    key={AppStore.storeId}
                    onClick={() => {
                      selectStore(AppStore);
                    }}
                    className="block"
                  >
                    <div className="flex flex-row gap-1">
                      <PinDrop sx={{ color: '#6B00D7' }} />
                      <ListItemText primary={AppStore.storeName} secondary={AppStore.storeDetail} />
                    </div>
                  </ListItemButton>
                ))}
              </List>
            )}
          </form>
          {hasGradientBorder && <div> </div>}
        </div>

        {(!showDropdown || searchWord?.length === 0) && <EmptyCompnent />}
      </div>
      {showSearchModal ? <SearchModal showModal={showSearchModal} handleShowModal={() => setShowSearchModal(!showSearchModal)} searchWord={searchWord} type="SEARCH" /> : ''}
    </>
  );
};

export default StoreSearchBar;
